import { TRIAL_DURATION_DAYS } from '../constants/index.js';

/**
 * Content access model (ENGINEERING_PROMPT §10).
 *
 * Access is entitlement-driven, never role-driven. A content item may declare
 * several valid access rules; a user needs to satisfy at least one of them.
 */

/*
  Тарифы — правила, а не отдельная система прав (docs/PAYMENTS.md, §4.1).

  `light` выполняется при действующем Лайт или Про: Про включает всё, что
  открывает Лайт. `pro` — только при Про. Что входит в тарифы, решают флажки
  на материалах, а не код: Дарья отмечает их в админке.
*/
export const ACCESS_RULES = ['free', 'trial', 'light', 'pro', 'game_owner', 'manual'] as const;
export type AccessRule = (typeof ACCESS_RULES)[number];

/*
  Подписи по смыслу правила, а не по его коду (уточнение 16.10.2026):
  `free` — это «всем, кто вошёл», а не «бесплатно для всех»; анонимного
  доступа нет. `manual` в редакторе больше не предлагается — точечная выдача
  из карточки пользователя открывает материал при любых правилах, — но в
  старых настройках правило может встретиться, поэтому подпись у него есть.
*/
export const ACCESS_RULE_LABELS: Record<AccessRule, string> = {
  free: 'Всем зарегистрированным',
  trial: `В пробный период (${TRIAL_DURATION_DAYS} дней)`,
  light: 'По тарифу «Лайт»',
  pro: 'По тарифу «Про»',
  game_owner: 'Владельцам игры',
  manual: 'Доступ выдан администратором',
};

/** Reason why a piece of content is locked, used to render the locked-state UX (§19). */
export const ACCESS_DENIAL_REASONS = [
  'not_authenticated',
  'trial_expired',
  'light_required',
  'pro_required',
  'game_ownership_required',
  'manual_grant_required',
  'not_published',
] as const;
export type AccessDenialReason = (typeof ACCESS_DENIAL_REASONS)[number];

export const ACCESS_DENIAL_LABELS: Record<AccessDenialReason, string> = {
  not_authenticated: 'Войдите, чтобы открыть материал',
  trial_expired: 'Пробный период закончился',
  light_required: 'Доступно в тарифе Лайт',
  pro_required: 'Доступно в тарифе Про',
  game_ownership_required: 'Доступно владельцам игры',
  manual_grant_required: 'Доступ открывает администратор',
  not_published: 'Материал ещё не опубликован',
};

/** Kinds of content the access engine can resolve. */
export const CONTENT_KINDS = ['deck', 'spread', 'training_material', 'game'] as const;
export type ContentKind = (typeof CONTENT_KINDS)[number];

export type AccessDecision =
  | { allowed: true; matchedRule: AccessRule }
  | { allowed: false; reason: AccessDenialReason; declaredRules: AccessRule[] };

/** Entitlements a registered user can hold (§12, §13). */
export const ENTITLEMENT_TYPES = ['subscription', 'game_ownership', 'manual_content'] as const;
export type EntitlementType = (typeof ENTITLEMENT_TYPES)[number];
